import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Medicine } from 'src/interfaces/medicine';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { AlertService } from './alert.service';
import {
  EditOrderFormData,
  Order,
  OrderSplitPayload,
  SplitOrderReportRow,
  SplitOrdersReportPayload,
  TrackingDetails,
  TrackingEvent,
} from 'src/interfaces/order';
import {
  Batch,
  BatchAutoCreateData,
  BatchCreatePayload,
  BatchItem,
  LinkOrderItemToBatchPayload,
  ScanBatchItemPayload,
} from 'src/interfaces/batch';
import { FulfilmentPayload, OrderItem } from 'src/interfaces/order-item';
import { UtilsService } from './utils.service';
import { filterSelectableDosageTypes } from '../utils/selectable-dosage-types';
import { DosageType } from 'src/interfaces/dosage-type';
import { BatchService } from './batch.service';
import { ConfirmationService } from './confirmation.service';
import { ShippingService } from './shipping.service';

@Injectable({
  providedIn: 'root',
})
export class PrescriptionService {
  baseUrl: string = environment.api.baseUrl;
  headers: HttpHeaders;
  totalNumberOfOrders: number = 0;
  totalNumberOfBatches: number = 0;
  totalNumberOfBatchItems: number = 0;

  constructor(
    private _auth: AuthService,
    private _alertService: AlertService,
    private http: HttpClient,
    private _utilsService: UtilsService,
    private _batchService: BatchService,
    private _confirmationService: ConfirmationService,
    private _shippingService: ShippingService
  ) {
    this._auth.generateRequestHeaders().then((headers) => {
      this.headers = headers;
    });
  }

  async getOrder(id: number): Promise<Order | null> {
    try {
      const { data } = await this.http
        .get<any>(`${this.baseUrl}/order/${id}`, {
          headers: this.headers,
        })
        .toPromise();
      const order: Order = data;
      const rawTrackingDetails = data.shipping_details.tracking_details.filter(
        (trackingDetail: any) => {
          try {
            const tracking = JSON.parse(trackingDetail.tracking);
            return true;
          } catch (error) {
            return false;
          }
        }
      );

      order.shipping_details.tracking_details = rawTrackingDetails.map(
        (trackingDetail: any) => {
          const tracking = JSON.parse(trackingDetail.tracking);

          const result: TrackingDetails = {
            id: 0,
            tracking,
            shipping_details_id: 0,
            created_at: '',
            updated_at: null,
            deleted_at: null,
          };

          return result;
        }
      );

      return order;
    } catch (error) {
      this._utilsService.showError(error);

      return null;
    }
  }

  async splitOrder(id: number, payload: OrderSplitPayload): Promise<Order[]> {
    const confirm: boolean = await this.confirm(
      'Split order',
      'Click confirm to split this order.'
    );
    if (!confirm) return [];
    try {
      const { data, success, message } = await this.http
        .post<any>(`${this.baseUrl}/order/${id}/split`, payload, {
          headers: this.headers,
        })
        .toPromise();

      if (success) this._alertService.openSnackBar(message);

      return data;
    } catch (error) {
      this._utilsService.showError(error);

      return [];
    }
  }

  getPrescriptions() {
    return this.http
      .get<any>(`${this.baseUrl}/order?page=1&limit=30`, {
        headers: this.headers,
      })
      .pipe(
        map((response) => response.data.results),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  async getOrders(
    page: number = 1,
    limit: number = 10,
    order: string = 'desc',
    status: string | null = null,
    order_by: string = 'created_at',
    search: string | null = null
  ): Promise<Order[]> {
    let queryString: string = `?page=${page}&limit=${limit}&order_direction=${order}&order_by=${order_by}`;

    if (status) queryString += `&order_status=${status}`;

    if (search) queryString += `&search=${search}`;

    try {
      const response = await this.http
        .get<any>(`${this.baseUrl}/order${queryString}`, {
          headers: this.headers,
        })
        .toPromise();
      
      const orders: Order[] = response.data.results;
      this.totalNumberOfOrders = response.data.total;
      return orders;
    } catch (error) {
      this._utilsService.showError(error);

      return [];
    }
  }

  async reportOrder(payload: {
    start_date: string;
    end_date: string;
  }): Promise<Order[]> {
    try {
      const response = await this.http
        .post<any>(`${this.baseUrl}/report/order`, payload)
        .toPromise();

      const orders: Order[] = response.data;

      return orders;
    } catch (error) {
      this._utilsService.showError(error);

      return [];
    }
  }

  async ShippingOrders(payload: {
    start_date: string;
    end_date: string;
  }): Promise<Order[]> {
    try {
      const response = await this.http
        .post<any>(`${this.baseUrl}/report/shipped`, payload)
        .toPromise();

      const orders: any[] = response.data;

      return orders;
    } catch (error) {
      this._utilsService.showError(error);

      return [];
    }
  }

  /**
   * Split orders report: POST report/split-orders. Same auth as other reports.
   * Returns data only when success === true; otherwise shows error and returns [].
   */
  async reportSplitOrders(
    payload: SplitOrdersReportPayload = {}
  ): Promise<SplitOrderReportRow[]> {
    try {
      const response = await this.http
        .post<{ success: boolean; message?: string; data?: SplitOrderReportRow[] }>(
          `${this.baseUrl}/report/split-orders`,
          payload,
          { headers: this.headers }
        )
        .toPromise();
      if (response?.success && Array.isArray(response.data)) {
        return response.data;
      }
      const msg = response?.message ?? 'Failed to fetch split orders';
      this._utilsService.showError({ error: { message: msg } });
      return [];
    } catch (error) {
      this._utilsService.showError(error);
      return [];
    }
  }

  /**
   * Manual Vetscripts daily shipped report: GET signed download URL for CSV.
   * @param date Optional YYYY-MM-DD; if omitted backend uses today (UTC).
   * @returns { url, expiresIn } or null on error.
   */
  async getVetscriptsShippedDailyReport(
    date?: string
  ): Promise<{ url: string; expiresIn: number } | null> {
    try {
      type VetscriptsReportResponse = {
        success: boolean;
        message?: string;
        data?: { url: string; expiresIn: number };
        errors?: { date?: string };
      };
      const url = date
        ? `${this.baseUrl}/report/vetscripts-shipped-daily?date=${encodeURIComponent(date)}`
        : `${this.baseUrl}/report/vetscripts-shipped-daily`;
      const response = await this.http
        .get<VetscriptsReportResponse>(url)
        .toPromise();
      if (response?.success && response?.data?.url) {
        return { url: response.data.url, expiresIn: response.data.expiresIn ?? 3600 };
      }
      // 200 but unexpected shape or success false
      const msg =
        response?.errors?.date ?? response?.message ?? 'Failed to generate report';
      this._utilsService.showError({ error: { message: msg } });
      return null;
    } catch (error: any) {
      const msg =
        error?.error?.errors?.date ??
        error?.error?.message ??
        error?.message ??
        'Failed to generate report';
      this._utilsService.showError({ error: { message: msg } });
      return null;
    }
  }

  isEntityAssigned(order: Order): boolean {
    if (!order) return false;

    return (
      order.patient !== null || order.doctor !== null || order.client !== null
    );
  }

  isMedicineAssigned(order: Order): boolean {
    if (!order) return false;

    for (let i = 0; i < order?.order_items?.length; i++) {
      const orderItem = order.order_items[i];
      if (orderItem.batch_items?.length == 0) return false;
    }
    return true;
  }

  hasOrderItems(order: Order) {
    return order ? order.order_items.length > 0 : false;
  }

  entityName(order: Order) {
    if (!order) return '';

    if (order.patient)
      return `${order.patient.first_name} ${order.patient.last_name}`;

    if (order.doctor)
      return order.doctor.company
        ? order.doctor.company.name
        : `${order.doctor.first_name} ${order.doctor.last_name}`;

    if (order.client) return order.client ? order.client.name : '';

    return '';
  }

  address(order: Order) {
    if (!order) return null;

    return order.shipping_details.address;
  }

  phoneNumber(order: Order) {
    if (!order) return '';

    if (order.patient) return order.patient.phone_number;

    if (order.doctor)
      return order.doctor.company
        ? order.doctor.company.phone_number
          ? order.doctor.company.phone_number
          : ''
        : order.doctor.phone_number;

    if (order.client)
      return order.client
        ? order.client.phone_number
          ? order.client.phone_number
          : ''
        : '';

    return '';
  }

  alternativePhoneNumber(order: Order) {
    if (!order) return '';

    if (order.patient) return order.patient.phone_number_alt;

    if (order.doctor)
      return order.doctor.company
        ? order.doctor.company.phone_number_alt
          ? order.doctor.company.phone_number_alt
          : ''
        : order.doctor.phone_number_alt;

    if (order.client)
      return order.client
        ? order.client.phone_number_alt
          ? order.client.phone_number_alt
          : ''
        : '';

    return '';
  }

  entityEmail(order: Order) {
    if (!order) return '';

    if (order.doctor)
      return order.doctor.company ? order.doctor.company.email : '';
    if (order.client) return order.client ? order.client.email : '';

    return '';
  }

  entityAlternativeEmail(order: Order) {
    if (!order) return '';

    if (order.doctor)
      return order.doctor.company ? order.doctor.company.email_alt : '';
    if (order.client) return order.client ? order.client.email_alt : '';

    return '';
  }

  getTotalNumberOfOrders() {
    return this.totalNumberOfOrders;
  }

  getIndividualTypes() {
    return this.http
      .post<any>(`${this.baseUrl}/form-data`, ['individual_type'], {
        headers: this.headers,
      })
      .pipe(
        map((response) => response.data.individual_type),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  async getMedicine(
    filter: string,
    page: number,
    limit: number
  ): Promise<Medicine[]> {
    try {
      const response = await this.http
        .get<any>(
          `${this.baseUrl}/medicine?page=${page}&limit=${limit}&search=${filter}`,
          {
            headers: this.headers,
          }
        )
        .toPromise();

      return response.data.results;
    } catch (error) {
      this._utilsService.showError(error);

      return [];
    }
  }

  getMedicineDosageTypes(medicineId: number) {
    return this.http
      .get<any>(
        `${
          this.baseUrl
        }/medicine/${medicineId}/dosage-type?page=${1}&limit=${99999}&stock`,
        {
          headers: this.headers,
        }
      )
      .pipe(
        map((response) =>
          filterSelectableDosageTypes<DosageType>(
            response.data.results ?? []
          ) as any
        ),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  getPackageTypes(medicineId: number, medicineDosageTypeId: number) {
    return this.http
      .get<any>(
        `${
          this.baseUrl
        }/medicine/${medicineId}/dosage-type/${medicineDosageTypeId}/package?page=${1}&limit=${10}&stock`,
        {
          headers: this.headers,
        }
      )
      .pipe(
        map((response) => response.data.results),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  getMedicineDosagePackageTypes(medicineId: number, dosageTypeId: number) {
    return this.http
      .get<any>(
        `${
          this.baseUrl
        }/medicine/${medicineId}/dosage-type/${dosageTypeId}/package?page=${1}&limit=${10}&stock`,
        {
          headers: this.headers,
        }
      )
      .pipe(
        map((response) => response.data.results),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  getDeliveryTypes() {
    return this.http
      .post<any>(`${this.baseUrl}/form-data`, ['delivery_type'], {
        headers: this.headers,
      })
      .pipe(
        map((response) => response.data.delivery_type),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  getFormData(formData: string[]) {
    return this.http
      .post<any>(`${this.baseUrl}/form-data`, formData, {
        headers: this.headers,
      })
      .pipe(
        map((response) => response.data),
        catchError((error: HttpErrorResponse) => {
          this._utilsService.showError(error);

          return throwError(error);
        })
      );
  }

  getAddresses(individualId: number) {
    return this.http
      .get<any>(`${this.baseUrl}/individual/${individualId}`, {
        headers: this.headers,
      })
      .pipe(
        map((response) => response.data.addresses),
        catchError((error: HttpErrorResponse) => {
          const _error: HttpErrorResponse = <HttpErrorResponse>error;
          this._alertService.errorAlert(_error.error.message);

          return throwError(error);
        })
      );
  }

  async generateOrderSummary(orderSummaryData: any) {
    if (!orderSummaryData) {
      this._alertService.errorAlert(
        "This order doesn't have any medicine attached to it"
      );
      return null;
    }
    try {
      const { data } = await this.http
        .post<any>(`${this.baseUrl}/prescription-pack`, orderSummaryData, {
          headers: this.headers,
        })
        .toPromise();

      const body = JSON.parse(data.body);
      return body.data;
    } catch (error) {
      this._utilsService.showError(error);
    }
  }

  async waiveOrderDeliveryFee(
    orderId: number
  ): Promise<{ delivery_fee_amount: number } | null> {
    try {
      const { success, message, data } = await this.http
        .post<any>(`${this.baseUrl}/order/${orderId}/delivery-fee/waive`, {}, {
          headers: this.headers,
        })
        .toPromise();
      if (success) this._alertService.openSnackBar(message);
      return success ? data : null;
    } catch (error: any) {
      this._utilsService.showError(error);
      return null;
    }
  }

  async editOrder(payload: EditOrderFormData, autoConfirm: boolean = false) {
    // open a dialog
    if (!autoConfirm) {
      const confirm = await this.confirmSave();

      if (!confirm) return false;
    }

    try {
      const { success, message } = await this.http
        .post<any>(`${this.baseUrl}/order/${payload.id}`, payload, {
          headers: this.headers,
        })
        .toPromise();
      this._alertService.infoAlert(message);
      return success;
    } catch (error: any) {
      this._utilsService.showError(error);

      return false;
    }
  }

  async editSplitOrder(payload: EditOrderFormData) {
    try {
      const { success, message } = await this.http
        .post<any>(`${this.baseUrl}/order/${payload.id}`, payload, {
          headers: this.headers,
        })
        .toPromise();
      this._alertService.openSnackBar(message);
      return success;
    } catch (error: any) {
      this._utilsService.showError(error);

      return false;
    }
  }

  async getBatches(
    page: number,
    limit: number,
    search: string = '',
    medicine_id: number | null = null,
    batch_status_id: number | null = null
  ): Promise<Batch[]> {
    return this._batchService.getBatches(
      page,
      limit,
      search,
      medicine_id,
      batch_status_id
    );
  }

  async searchBatches(
    page: number,
    limit: number,
    search: string
  ): Promise<Batch[]> {
    return this._batchService.searchBatches(page, limit, search);
  }

  getTotalNumberOfBatches() {
    return this._batchService.getTotalNumberOfBatches();
  }

  async getBatch(id: number): Promise<Batch | null> {
    return this._batchService.getBatch(id);
  }

  async createBatch(payload: BatchCreatePayload) {
    return this._batchService.createBatch(payload);
  }

  async requestBatch(payload: BatchAutoCreateData) {
    return this._batchService.requestBatch(payload);
  }

  async linkToBatch(batchId: number, payload: LinkOrderItemToBatchPayload) {
    return this._batchService.linkToBatch(batchId, payload);
  }

  async editBatch(payload: BatchCreatePayload, batchId: number) {
    return this._batchService.editBatch(payload, batchId);
  }

  confirmSave(): Promise<boolean> {
    return this._confirmationService.confirmSave();
  }

  confirm(title: string, message: string): Promise<boolean> {
    return this._confirmationService.confirm(title, message);
  }

  confirmBatchRequest(
    title: string,
    message: string
  ): Promise<{
    confirm: boolean;
    batch_number: string;
  }> {
    return this._confirmationService.confirmBatchRequest(title, message);
  }

  openLinkToBatchDialog(
    title: string,
    message: string,
    orderItem: OrderItem
  ): Promise<boolean> {
    return this._batchService.openLinkToBatchDialog(title, message, orderItem);
  }

  openOrderFulfilmentDialog(
    order: Order,
    orderItems: OrderItem[],
    title: string = 'Order fulfilment',
    message: string = 'Order fulfilment'
  ): Promise<boolean> {
    return this._batchService.openOrderFulfilmentDialog(
      order,
      orderItems,
      title,
      message
    );
  }

  async openBatchItemListDialog(
    orderItem: OrderItem,
    title: string = 'Batch items',
    message: string = 'Batch items for fulfilment'
  ): Promise<boolean> {
    return this._batchService.openBatchItemListDialog(
      orderItem,
      title,
      message
    );
  }

  async getBatchItems(
    orderItemId: number,
    page: number,
    limit: number,
    search: string = ''
  ): Promise<BatchItem[]> {
    return this._batchService.getBatchItems(orderItemId, page, limit, search);
  }

  getTotalNumberOfBatchItems(): number {
    return this._batchService.getTotalNumberOfBatchItems();
  }

  async scanBatchItem(
    orderItemId: number,
    batchItemId: number,
    payload: ScanBatchItemPayload
  ): Promise<boolean> {
    return this._batchService.scanBatchItem(orderItemId, batchItemId, payload);
  }

  async scanOrderItem(
    orderItemId: number,
    payload: FulfilmentPayload
  ): Promise<boolean> {
    return this._batchService.scanOrderItem(orderItemId, payload);
  }

  getOrderItems(order: Order) {
    if (!order) return [];

    return order.order_items.map((item: any) => {
      const orderedItem = {
        id: item.id,
        order_id: item.order_id,
        medicineDosagePackageType: item.medicine_dosage_package_type,
        dosageType: item.medicine_dosage_package_type.medicine_dosage_type,
        status: item.status,
        medicine:
          item.medicine_dosage_package_type.medicine_dosage_type.medicine,
        quantity: item.total_quantity,
        medicine_dosage_package_type_id: item.medicine_dosage_package_type_id,
        quantity_fulfilled: item.quantity_fulfilled,
        deleted_at: undefined,
        batch_items: item.batch_items,
        patient: item.patient ?? null,
      };

      orderedItem.dosageType.default_price =
        item.medicine_dosage_package_type.medicine_dosage_type.default_price;

      return orderedItem;
    });
  }

  async waybillOrder(order: Order): Promise<boolean> {
    
    const confirm = await this._confirmationService.confirm(
      'Waybill Order',
      'Will this be shipped with another order?'
    );

    if (!confirm) {
      await this._shippingService.openShippingFormDialog(order);
      return false;
    }

    try {
      await this._shippingService.openStatusPackingDialog(order);
      return false;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }
  

  async EditOrderDialog(order: Order): Promise<boolean> {
   
    try {
      await this._shippingService.openEditOrderDialog(order);
      return true;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

 
}
